import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Form, Button, Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { createChatbotConfig, updateChatbotConfig, getChatbotConfigById } from './FormikHelpers/Api';

const NODE_TYPES = [
  { value: 'simple_reply', label: 'Text Reply', icon: '💬', color: '#3B82F6', group: 'messaging' },
  { value: 'form', label: 'Collect Form', icon: '📋', color: '#8B5CF6', group: 'capture' },
  { value: 'booking_link', label: 'Booking Link', icon: '📅', color: '#10B981', group: 'action' },
  { value: 'ai_reply', label: 'AI Reply', icon: '🤖', color: '#F59E0B', group: 'messaging' },
  { value: 'card_info', label: 'Info Card', icon: '🃏', color: '#EC489A', group: 'messaging' },
  { value: 'property_listing', label: 'Property Listing', icon: '🏠', color: '#14B8A6', group: 'property' },
  { value: 'property_details', label: 'Property Details', icon: '🔍', color: '#F97316', group: 'property' },
  { value: 'conditional_branch', label: 'Branch / Filter', icon: '🔀', color: '#6366F1', group: 'logic' },
  { value: 'email_notify', label: 'Email Notify', icon: '📧', color: '#0EA5E9', group: 'action' },
];
const FIELD_TYPES = ['text', 'email', 'tel', 'textarea', 'select', 'radio', 'checkbox', 'number', 'date'];
const AI_MODELS = [
  { value: 'anthropic.claude-haiku-4-5-20251001-v1:0', label: 'Claude Haiku 4.5 — fastest' },
  { value: 'anthropic.claude-sonnet-4-6-v1:0', label: 'Claude Sonnet 4.6 — smarter' },
  { value: 'anthropic.claude-opus-4-6-v1:0', label: 'Claude Opus 4.6 — most capable' },
];
const PROPERTY_TYPES = ['Apartment', 'Townhouse', 'Condo', 'House', 'Studio', 'Loft'];
const LOCATION_OPTIONS = ['Downtown Calgary', 'St. Albert', 'Chappelle', 'Windermere', 'Sherwood Park', 'Leduc'];

const slugify = (str) => str.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

const generateNodeId = (label = 'New Option', parentId = null, existingIds = new Set()) => {
  let base = slugify(label);
  if (parentId) base = `${parentId}_${base}`;
  let id = base;
  let counter = 1;
  while (existingIds.has(id)) {
    id = `${base}_${counter}`;
    counter++;
  }
  return id;
};

const makeNode = (overrides = {}, parentId = null, existingIds = new Set()) => ({
  id: generateNodeId(overrides.label || 'New Option', parentId, existingIds),
  type: 'simple_reply',
  label: 'New Option',
  icon: '💡',
  description: '',
  order: 0,
  enabled: true,
  triggers: [],
  replies: ['Sure! How can I help?'],
  show_children_as_options: true,
  children: [],
  next_node_id: null,
  form_fields: [],
  form_submit_intent: '',
  form_next_node_id: null,
  form_notify_email: '',
  form_notify_name: '',
  booking_url: '',
  booking_label: 'Book Appointment',
  booking_next_node_id: null,
  ai_model: '',
  ai_system_prompt: '',
  ai_max_tokens: 0,
  card_title: '',
  card_description: '',
  card_image_url: '',
  card_button_label: '',
  card_button_url: '',
  property_filters: {},
  listing_layout: 'list',
  property_id: null,
  branch_variable: '',
  branch_options: [],
  notify_email: '',
  notify_name: '',
  notify_subject: '',
  notify_template: 'CHATBOT_INQUIRY',
  delay_ms: 0,
  ...overrides,
});

const makeTrigger = () => ({ keywords: [], patterns: [], exact_texts: [], enabled: true, priority: 0 });
const makeFormField = (order = 0) => ({ id: `field_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, name: '', label: '', type: 'text', required: false, placeholder: '', options: [], order });
const makeBranchOption = () => ({ label: '', value: '', next_node_id: '' });

const makeStarterFlow = () => {
  const greetingId = 'greeting';
  const leadFormId = 'greeting_lead_capture_form';
  const thankYouId = 'greeting_thank_you';
  const fallbackFormId = 'fallback_inquiry_form';
  const fallbackDoneId = 'fallback_done';
  return [
    makeNode({ id: greetingId, label: 'Greeting', icon: '👋', type: 'simple_reply', order: 0, replies: ["Hello! Welcome to our service. How can we assist you today?"], children: [leadFormId] }, null),
    makeNode({ id: leadFormId, label: 'Lead Capture Form', icon: '📋', type: 'form', order: 0, replies: ["Please fill in your details below so we can get back to you."], form_fields: [{ ...makeFormField(0), name: 'first_name', label: 'First Name', type: 'text', required: true, placeholder: 'John' }, { ...makeFormField(1), name: 'last_name', label: 'Last Name', type: 'text', required: true, placeholder: 'Smith' }, { ...makeFormField(2), name: 'email', label: 'Email Address', type: 'email', required: true, placeholder: 'john@example.com' }], form_submit_intent: `${leadFormId}_submit`, form_next_node_id: thankYouId }, greetingId),
    makeNode({ id: thankYouId, label: 'Thank You', icon: '🎉', type: 'simple_reply', order: 0, replies: ["Thank you! Our team will reach out soon."] }, leadFormId),
    makeNode({ id: fallbackFormId, label: 'General Inquiry', icon: '✉️', type: 'form', order: 99, replies: ["Please tell us more about your inquiry."], form_fields: [{ ...makeFormField(0), name: 'first_name', label: 'First Name', type: 'text', required: true, placeholder: 'John' }, { ...makeFormField(1), name: 'email', label: 'Email Address', type: 'email', required: true, placeholder: 'john@example.com' }, { ...makeFormField(2), name: 'message', label: 'Message', type: 'textarea', required: true, placeholder: 'Your message...' }], form_submit_intent: `${fallbackFormId}_submit`, form_next_node_id: fallbackDoneId }, null),
    makeNode({ id: fallbackDoneId, label: 'Inquiry Submitted', icon: '📬', type: 'simple_reply', order: 0, replies: ["Thank you for your message. We'll get back to you soon."] }, fallbackFormId),
  ];
};

const emptyConfig = () => ({
  chat_slug: '',
  branding: { bot_name: 'Chatbot', bot_slug: '', launcher_icon: '🤖', launcher_icon_url: '', logo_icon: '🤖', logo_icon_url: '', closing_icon: '✖', closing_icon_url: '', primary_color: '#1D4ED8', secondary_color: '#3B82F6', text_color: '#ffffff', background_color: '#ffffff', font_family: 'system-ui, -apple-system, "Segoe UI"', chat_window_width: 380, chat_window_height: 600, position: 'bottom-right', offset_x: 20, offset_y: 20, custom_css: '', placeholder_text: 'Type a message...' },
  flow_nodes: makeStarterFlow(),
  fallback_node_id: 'fallback_inquiry_form',
  integrations: { booking_url: '', crm_webhook: '', notification_email: '', suite_api_enabled: false, property_id: '', building_name: '', margo_email: '' },
  ai: { enabled: false, model: 'anthropic.claude-haiku-4-5-20251001-v1:0', knowledge_base: '', system_prompt: '', fallback_message: "I'm not sure about that — but I'd love to help! Please use the form below and our team will assist you.", max_tokens: 300 },
});

function updateNodeInList(nodes, id, updater) {
  return nodes.map((n) => n.id === id ? updater(n) : n);
}

function deleteNodeFromList(nodes, id) {
  return nodes
    .filter((n) => n.id !== id)
    .map((n) => ({ ...n, children: (n.children || []).filter((cid) => cid !== id) }));
}

function findParentId(nodes, childId) {
  for (const n of nodes) {
    if ((n.children || []).includes(childId)) return n.id;
  }
  return null;
}

function renameNodeId(nodes, oldId, newId, fallbackNodeId) {
  const updatedNodes = nodes.map((n) => {
    let updated = { ...n };
    if (n.id === oldId) updated.id = newId;
    updated.children = (n.children || []).map((cid) => (cid === oldId ? newId : cid));
    if (n.next_node_id === oldId) updated.next_node_id = newId;
    if (n.form_next_node_id === oldId) updated.form_next_node_id = newId;
    if (n.booking_next_node_id === oldId) updated.booking_next_node_id = newId;
    if (n.branch_options && n.branch_options.length) {
      updated.branch_options = n.branch_options.map((bo) =>
        bo.next_node_id === oldId ? { ...bo, next_node_id: newId } : bo
      );
    }
    return updated;
  });
  const updatedFallback = fallbackNodeId === oldId ? newId : fallbackNodeId;
  return { nodes: updatedNodes, fallbackNodeId: updatedFallback };
}

function getAllReferencedIds(nodes) {
  const referenced = new Set();
  nodes.forEach((n) => {
    (n.children || []).forEach((cid) => referenced.add(cid));
    if (n.next_node_id) referenced.add(n.next_node_id);
    if (n.form_next_node_id) referenced.add(n.form_next_node_id);
    if (n.booking_next_node_id) referenced.add(n.booking_next_node_id);
    if (n.branch_options && n.branch_options.length) {
      n.branch_options.forEach((bo) => { if (bo.next_node_id) referenced.add(bo.next_node_id); });
    }
  });
  return referenced;
}

function buildTree(nodes) {
  const uniqueNodes = Array.from(new Map(nodes.map(n => [n.id, n])).values());
  const nodeMap = {};
  uniqueNodes.forEach((n) => { nodeMap[n.id] = { ...n, _children: [] }; });
  const referenced = getAllReferencedIds(uniqueNodes);
  const roots = uniqueNodes.filter((n) => !referenced.has(n.id));
  
  const buildChildren = (nodeId, visited = new Set()) => {
    if (visited.has(nodeId)) return [];
    visited.add(nodeId);
    const n = nodeMap[nodeId];
    if (!n) return [];
    const children = (n.children || []).flatMap((cid) => buildChildren(cid, visited));
    return [{
      ...n,
      _children: children
    }];
  };
  
  const tree = roots.flatMap((r) => buildChildren(r.id));
  return tree;
}

const nodeTypeMeta = (type) => NODE_TYPES.find((t) => t.value === type) || NODE_TYPES[0];

const inp = { fontSize: 12.5, border: '1.5px solid #E2E8F0', borderRadius: 8, padding: '7px 10px', outline: 'none', width: '100%', fontFamily: 'inherit', background: '#fff', color: '#0F172A', transition: 'border-color .15s' };
const lbl = { fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.5px', display: 'block', marginBottom: 4 };
const pill = (active, color = '#1D4ED8') => ({ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: `1.5px solid ${active ? color : '#E2E8F0'}`, background: active ? `${color}15` : '#fff', color: active ? color : '#94A3B8', transition: 'all .12s', userSelect: 'none' });

const TagInput = ({ values = [], onChange, placeholder = 'Type and press Enter…' }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v || values.includes(v)) return;
    onChange([...values, v]);
    setDraft('');
  };
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 5, minHeight: 24 }}>
        {values.map((v, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 20, padding: '2px 8px', fontSize: 11, color: '#1D4ED8', fontWeight: 600 }}>
            {v}
            <button type="button" onClick={() => onChange(values.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: 12, padding: 0, lineHeight: 1 }}>×</button>
          </span>
        ))}
        {values.length === 0 && <span style={{ fontSize: 11, color: '#CBD5E1', fontStyle: 'italic' }}>None yet</span>}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input style={{ ...inp, flex: 1 }} value={draft} placeholder={placeholder} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
        <button type="button" onClick={add} style={{ padding: '5px 12px', borderRadius: 7, border: '1.5px solid #BFDBFE', background: '#EFF6FF', color: '#1D4ED8', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Add</button>
      </div>
    </div>
  );
};

const RepliesEditor = ({ replies = [], onChange }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    if (!draft.trim()) return;
    onChange([...replies, draft.trim()]);
    setDraft('');
  };
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6, minHeight: 28 }}>
        {replies.map((r, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 6, padding: '3px 10px', fontSize: 11, color: '#059669', maxWidth: 280 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.length > 60 ? r.slice(0, 60) + '…' : r}</span>
            <button type="button" onClick={() => onChange(replies.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: 12, padding: 0, lineHeight: 1, flexShrink: 0 }}>×</button>
          </span>
        ))}
        {replies.length === 0 && <span style={{ fontSize: 11, color: '#CBD5E1', fontStyle: 'italic' }}>No replies yet</span>}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input style={{ ...inp, flex: 1 }} value={draft} placeholder="Type a reply variant…" onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
        <button type="button" onClick={add} style={{ padding: '5px 12px', borderRadius: 7, border: '1.5px solid #BBF7D0', background: '#F0FDF4', color: '#059669', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Add</button>
      </div>
      <p style={{ fontSize: 10, color: '#94A3B8', marginTop: 4 }}>One variant is picked randomly each time this node fires.</p>
    </div>
  );
};

const TriggerEditor = ({ triggers = [], onChange }) => {
  const addTrigger = () => onChange([...triggers, makeTrigger()]);
  const removeTrigger = (i) => onChange(triggers.filter((_, j) => j !== i));
  const updateTrigger = (i, key, val) => onChange(triggers.map((t, j) => j === i ? { ...t, [key]: val } : t));
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#F59E0B', textTransform: 'uppercase', letterSpacing: '.5px' }}>⚡ Auto-Triggers</div>
        <button type="button" onClick={addTrigger} style={{ padding: '3px 10px', borderRadius: 6, border: '1.5px dashed #FDE68A', background: '#FFFBEB', color: '#D97706', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>+ Add Trigger</button>
      </div>
      {triggers.length === 0 && (
        <div style={{ background: '#FFFBEB', border: '1px dashed #FDE68A', borderRadius: 8, padding: '8px 12px', fontSize: 11, color: '#92400E' }}>
          No triggers — this node only fires when clicked as a button.
        </div>
      )}
      {triggers.map((t, i) => (
        <div key={i} style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#92400E' }}>Trigger #{i + 1}</span>
              <input type="number" value={t.priority} onChange={(e) => updateTrigger(i, 'priority', parseInt(e.target.value) || 0)} style={{ ...inp, width: 60, fontSize: 11 }} placeholder="Priority" />
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, cursor: 'pointer', color: '#92400E' }}>
                <input type="checkbox" checked={t.enabled} onChange={(e) => updateTrigger(i, 'enabled', e.target.checked)} />Active
              </label>
            </div>
            <button type="button" onClick={() => removeTrigger(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: 16, lineHeight: 1 }}>×</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <div><div style={{ ...lbl, color: '#B45309' }}>Keywords</div><TagInput values={t.keywords || []} onChange={(v) => updateTrigger(i, 'keywords', v)} placeholder="hello, pricing…" /></div>
            <div><div style={{ ...lbl, color: '#B45309' }}>Exact match</div><TagInput values={t.exact_texts || []} onChange={(v) => updateTrigger(i, 'exact_texts', v)} placeholder="what is your price" /></div>
            <div><div style={{ ...lbl, color: '#B45309' }}>Regex patterns</div><TagInput values={t.patterns || []} onChange={(v) => updateTrigger(i, 'patterns', v)} placeholder="\bpric(e|ing)\b" /></div>
          </div>
        </div>
      ))}
    </div>
  );
};

const FormFieldEditor = ({ fields = [], onChange }) => {
  const dragIndexRef = useRef(null);
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const upd = (i, k, v) => onChange(fields.map((f, j) => j === i ? { ...f, [k]: v } : f));
  const add = () => onChange([...fields, makeFormField(fields.length)]);
  const del = (i) => onChange(fields.filter((_, j) => j !== i));
  const duplicate = (i) => {
    const orig = fields[i];
    const copy = { ...orig, id: `field_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, label: orig.label ? `${orig.label} (copy)` : 'New Field', order: i + 1 };
    const nf = [...fields];
    nf.splice(i + 1, 0, copy);
    onChange(nf.map((f, idx) => ({ ...f, order: idx })));
  };
  const handleDragStart = (i) => { dragIndexRef.current = i; setDraggingIndex(i); };
  const handleDragOver = (e, i) => { e.preventDefault(); setDragOverIndex(i); };
  const handleDrop = (i) => {
    const from = dragIndexRef.current;
    if (from === null || from === i) { setDraggingIndex(null); setDragOverIndex(null); return; }
    const nf = Array.from(fields);
    const [moved] = nf.splice(from, 1);
    nf.splice(i, 0, moved);
    onChange(nf.map((f, idx) => ({ ...f, order: idx })));
    dragIndexRef.current = null;
    setDraggingIndex(null);
    setDragOverIndex(null);
  };
  const handleDragEnd = () => { dragIndexRef.current = null; setDraggingIndex(null); setDragOverIndex(null); };
  return (
    <div>
      {fields.map((f, i) => (
        <div key={f.id} draggable onDragStart={() => handleDragStart(i)} onDragOver={(e) => handleDragOver(e, i)} onDrop={() => handleDrop(i)} onDragEnd={handleDragEnd}
          style={{ background: draggingIndex === i ? '#EFF6FF' : dragOverIndex === i ? '#DBEAFE' : '#F8FAFC', border: `1px solid ${dragOverIndex === i ? '#3B82F6' : '#E2E8F0'}`, borderRadius: 8, padding: '10px 12px', marginBottom: 8, opacity: draggingIndex === i ? 0.5 : 1, cursor: 'grab', transition: 'all .1s' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ cursor: 'grab', fontSize: 20, color: '#64748B', padding: '4px 0' }}>≡</div>
            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 90px 1fr', gap: 8, alignItems: 'end' }}>
              <div>
                <label style={lbl}>Label *</label>
                <input style={inp} value={f.label} onChange={(e) => { upd(i, 'label', e.target.value); upd(i, 'name', e.target.value.toLowerCase().replace(/\s+/g, '_')); }} placeholder="Your Name" />
              </div>
              <div>
                <label style={lbl}>Type</label>
                <select style={inp} value={f.type} onChange={(e) => upd(i, 'type', e.target.value)}>
                  {FIELD_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Placeholder</label>
                <input style={inp} value={f.placeholder} onChange={(e) => upd(i, 'placeholder', e.target.value)} placeholder="Optional…" />
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <button type="button" onClick={() => duplicate(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3B82F6', fontSize: 16, lineHeight: 1 }}>⧉</button>
              <button type="button" onClick={() => del(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: 18, lineHeight: 1, paddingBottom: 4 }}>×</button>
            </div>
          </div>
          {(f.type === 'select' || f.type === 'radio') && (
            <div style={{ marginTop: 8 }}>
              <label style={lbl}>Options (comma-separated)</label>
              <input style={inp} value={(f.options || []).join(', ')} onChange={(e) => upd(i, 'options', e.target.value.split(',').map((x) => x.trim()).filter(Boolean))} placeholder="Option A, Option B" />
            </div>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 11, cursor: 'pointer', color: '#374151' }}>
            <input type="checkbox" checked={f.required} onChange={(e) => upd(i, 'required', e.target.checked)} />Required
          </label>
        </div>
      ))}
      <button type="button" onClick={add} style={{ fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: '5px 12px', borderRadius: 6, border: '1.5px dashed #C4B5FD', background: '#F5F3FF', color: '#7C3AED', marginTop: 4 }}>+ Add Field</button>
    </div>
  );
};

const BranchOptionsEditor = ({ options = [], allNodes = [], onChange }) => {
  const upd = (i, k, v) => onChange(options.map((o, j) => j === i ? { ...o, [k]: v } : o));
  const add = () => onChange([...options, makeBranchOption()]);
  const del = (i) => onChange(options.filter((_, j) => j !== i));
  return (
    <div>
      {options.map((o, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 8, alignItems: 'end', marginBottom: 8, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: '10px 12px' }}>
          <div><label style={lbl}>Button Label</label><input style={inp} value={o.label} onChange={(e) => upd(i, 'label', e.target.value)} placeholder="2 Bedroom" /></div>
          <div><label style={lbl}>Value (stored)</label><input style={{ ...inp, fontFamily: 'monospace', fontSize: 11 }} value={o.value} onChange={(e) => upd(i, 'value', e.target.value.toLowerCase().replace(/\s+/g, '_'))} placeholder="2_bedroom" /></div>
          <div>
            <label style={lbl}>Go to Node</label>
            <select style={inp} value={o.next_node_id || ''} onChange={(e) => upd(i, 'next_node_id', e.target.value)}>
              <option value="">— select —</option>
              {allNodes.map((n) => <option key={n.id} value={n.id}>{n.icon || ''} {n.label}</option>)}
            </select>
          </div>
          <button type="button" onClick={() => del(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: 18, lineHeight: 1, paddingBottom: 4 }}>×</button>
        </div>
      ))}
      <button type="button" onClick={add} style={{ fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: '5px 12px', borderRadius: 6, border: '1.5px dashed #A5B4FC', background: '#EEF2FF', color: '#6366F1', marginTop: 4 }}>+ Add Branch Option</button>
    </div>
  );
};

const NodeWarnings = ({ node, allNodes }) => {
  const warnings = [];
  if (!node.replies?.length) warnings.push('No replies — bot will be silent');
  if (node.type === 'form' && !node.form_fields?.length) warnings.push('Form has no fields');
  if (node.type === 'booking_link' && !node.booking_url) warnings.push('No booking URL set');
  if (node.type === 'conditional_branch' && !node.branch_options?.length) warnings.push('No branch options defined');
  if (node.type === 'email_notify' && !node.notify_email) warnings.push('No notification email set');
  const nodeIds = new Set(allNodes.map((n) => n.id));
  const brokenChildren = (node.children || []).filter((id) => !nodeIds.has(id));
  if (brokenChildren.length) warnings.push(`Broken child IDs: ${brokenChildren.join(', ')}`);
  if (!warnings.length) return null;
  return (
    <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
      {warnings.map((w, i) => (
        <div key={i} style={{ fontSize: 11, color: '#92400E', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>⚠️</span>{w}
        </div>
      ))}
    </div>
  );
};

const IntentIdEditor = ({ nodeId }) => (
  <div>
    <label style={lbl}>Intent ID (auto-generated)</label>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8 }}>
      <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#0F172A', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nodeId}</span>
      <button type="button" onClick={() => { navigator.clipboard?.writeText(nodeId); toast.success('Copied!', { autoClose: 900 }); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: 13, padding: 0 }} title="Copy ID">⧉</button>
    </div>
    <p style={{ fontSize: 10, color: '#94A3B8', marginTop: 5 }}>Hierarchical slug based on parent + label (lowercase + _). Updated automatically on rename.</p>
  </div>
);

const NodeEditor = ({ node, onUpdate, onClose, allNodes, propertyList }) => {
  if (!node) return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#CBD5E1', gap: 12, padding: 32 }}>
      <div style={{ width: 64, height: 64, borderRadius: 20, background: 'linear-gradient(135deg, #F0F9FF, #DBEAFE)', border: '2px solid #BFDBFE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>🌿</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#94A3B8' }}>Select a node to edit</div>
      <div style={{ fontSize: 12, textAlign: 'center', maxWidth: 220, color: '#CBD5E1', lineHeight: 1.6 }}>Click any node in the tree on the left to configure it here.</div>
    </div>
  );
  const u = (k, v) => onUpdate({ ...node, [k]: v });
  const meta = nodeTypeMeta(node.type);
  const isGreeting = node.id === 'greeting';
  const otherNodes = allNodes.filter((n) => n.id !== node.id);
  const childIds = (node.children || []);
  const addChild = (id) => { if (!childIds.includes(id)) u('children', [...childIds, id]); };
  const removeChild = (id) => u('children', childIds.filter((c) => c !== id));
  const sec = (bg, border) => ({ background: bg, border: `1px solid ${border}`, borderRadius: 12, padding: '14px 16px', marginBottom: 0 });
  return (
    <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ background: `linear-gradient(135deg, ${meta.color}18, ${meta.color}08)`, borderBottom: `2px solid ${meta.color}30`, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, background: `${meta.color}20`, border: `2px solid ${meta.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>{node.icon || meta.icon}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#0F172A', lineHeight: 1.2 }}>{node.label || 'Untitled'}</div>
          <div style={{ fontSize: 11, color: meta.color, fontWeight: 700, marginTop: 2 }}>
            {meta.label} <span style={{ color: '#CBD5E1' }}>·</span> <span style={{ fontFamily: 'monospace', color: '#94A3B8', fontWeight: 500 }}>{node.id}</span>
          </div>
        </div>
        <button type="button" onClick={onClose} style={{ background: 'rgba(0,0,0,0.06)', border: 'none', cursor: 'pointer', color: '#64748B', fontSize: 16, lineHeight: 1, width: 30, height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <NodeWarnings node={node} allNodes={allNodes} />
        {!isGreeting && (
          <div style={sec('#FAFBFF', '#E2E8F0')}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 10 }}>Node Type</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {NODE_TYPES.map((t) => (
                <button key={t.value} type="button" style={pill(node.type === t.value, t.color)} onClick={() => u('type', t.value)}>
                  {t.icon} {t.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {!isGreeting && (
          <div style={sec('#FAFBFF', '#E2E8F0')}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 10 }}>Identity</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: 10, marginBottom: 12 }}>
              <div>
                <label style={lbl}>Button Label</label>
                <input style={inp} value={node.label} onChange={(e) => u('label', e.target.value)} placeholder="e.g. Book a Tour" />
              </div>
              <div>
                <label style={lbl}>Icon</label>
                <input style={{ ...inp, textAlign: 'center', fontSize: 18 }} value={node.icon} onChange={(e) => u('icon', e.target.value)} placeholder="🏠" />
              </div>
            </div>
            <IntentIdEditor nodeId={node.id} />
          </div>
        )}
        <div style={sec('#FAFBFF', '#E2E8F0')}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 10 }}>Settings</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><label style={lbl}>Order</label><input type="number" style={inp} value={node.order} onChange={(e) => u('order', parseInt(e.target.value) || 0)} /></div>
            <div><label style={lbl}>Typing delay (ms)</label><input type="number" style={inp} value={node.delay_ms || 0} onChange={(e) => u('delay_ms', parseInt(e.target.value) || 0)} min={0} max={5000} /></div>
            {!isGreeting && (
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer', color: '#374151', padding: '8px 10px', background: node.enabled !== false ? '#F0FDF4' : '#FEF2F2', border: `1px solid ${node.enabled !== false ? '#BBF7D0' : '#FECACA'}`, borderRadius: 8 }}>
                  <input type="checkbox" checked={node.enabled !== false} onChange={(e) => u('enabled', e.target.checked)} />
                  <span style={{ fontWeight: 600, color: node.enabled !== false ? '#059669' : '#DC2626' }}>Node {node.enabled !== false ? 'Enabled' : 'Disabled'}</span>
                </label>
              </div>
            )}
          </div>
        </div>
        <div style={sec('#F0FDF4', '#BBF7D0')}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#059669', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 10 }}>
            💬 Bot Replies <span style={{ fontSize: 10, fontWeight: 500, color: '#6EE7B7' }}>(random pick)</span>
          </div>
          <RepliesEditor replies={node.replies || []} onChange={(v) => u('replies', v)} />
        </div>
        {node.type === 'booking_link' && (
          <div style={sec('#F0FDF4', '#BBF7D0')}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#059669', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 10 }}>📅 Booking Link</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
              <div><label style={lbl}>URL</label><input style={inp} value={node.booking_url} onChange={(e) => u('booking_url', e.target.value)} placeholder="https://calendly.com/…" /></div>
              <div><label style={lbl}>Button Label</label><input style={inp} value={node.booking_label || 'Book Now'} onChange={(e) => u('booking_label', e.target.value)} /></div>
            </div>
            <div>
              <label style={lbl}>After Booking → Go to Node</label>
              <select style={inp} value={node.booking_next_node_id || ''} onChange={(e) => u('booking_next_node_id', e.target.value || null)}>
                <option value="">— show child buttons —</option>
                {otherNodes.map((n) => <option key={n.id} value={n.id}>{n.icon} {n.label}</option>)}
              </select>
            </div>
          </div>
        )}
        {node.type === 'form' && (
          <div style={sec('#F5F3FF', '#DDD6FE')}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 10 }}>📋 Form Fields</div>
            <FormFieldEditor fields={node.form_fields || []} onChange={(v) => u('form_fields', v)} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
              <div><label style={lbl}>Submit Intent ID</label><input style={{ ...inp, fontFamily: 'monospace', fontSize: 11 }} value={node.form_submit_intent} onChange={(e) => u('form_submit_intent', e.target.value)} placeholder={`${node.id}_submit`} /></div>
              <div>
                <label style={lbl}>After Submit → Go to Node</label>
                <select style={inp} value={node.form_next_node_id || ''} onChange={(e) => u('form_next_node_id', e.target.value || null)}>
                  <option value="">— show child buttons —</option>
                  {otherNodes.map((n) => <option key={n.id} value={n.id}>{n.icon} {n.label}</option>)}
                </select>
              </div>
              <div><label style={lbl}>Notify Email</label><input type="email" style={inp} value={node.form_notify_email || ''} onChange={(e) => u('form_notify_email', e.target.value)} placeholder="team@example.com" /></div>
              <div><label style={lbl}>Recipient Name</label><input style={inp} value={node.form_notify_name || ''} onChange={(e) => u('form_notify_name', e.target.value)} placeholder="Team" /></div>
            </div>
          </div>
        )}
        {node.type === 'ai_reply' && (
          <div style={sec('#FFFBEB', '#FDE68A')}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#D97706', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 10 }}>🤖 AI Settings</div>
            <div style={{ marginBottom: 8 }}>
              <label style={lbl}>Model</label>
              <select style={inp} value={node.ai_model} onChange={(e) => u('ai_model', e.target.value)}>
                <option value="">Use config default</option>
                {AI_MODELS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 8 }}><label style={lbl}>System Prompt override</label><textarea style={{ ...inp, resize: 'vertical', minHeight: 60 }} value={node.ai_system_prompt} onChange={(e) => u('ai_system_prompt', e.target.value)} placeholder="Leave blank to use config default…" /></div>
            <div><label style={lbl}>Max tokens (0 = config default)</label><input type="number" style={{ ...inp, width: 100 }} value={node.ai_max_tokens} onChange={(e) => u('ai_max_tokens', parseInt(e.target.value) || 0)} min={0} max={4096} /></div>
          </div>
        )}
        {node.type === 'card_info' && (
          <div style={sec('#FCE7F3', '#FBCFE8')}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#DB2777', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 10 }}>🃏 Info Card</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div><label style={lbl}>Title</label><input style={inp} value={node.card_title} onChange={(e) => u('card_title', e.target.value)} /></div>
              <div><label style={lbl}>Image URL</label><input style={inp} value={node.card_image_url} onChange={(e) => u('card_image_url', e.target.value)} /></div>
              <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Description</label><textarea style={{ ...inp, resize: 'vertical', minHeight: 80 }} value={node.card_description} onChange={(e) => u('card_description', e.target.value)} /></div>
              <div><label style={lbl}>Button Label</label><input style={inp} value={node.card_button_label} onChange={(e) => u('card_button_label', e.target.value)} /></div>
              <div><label style={lbl}>Button URL</label><input style={inp} value={node.card_button_url} onChange={(e) => u('card_button_url', e.target.value)} /></div>
            </div>
          </div>
        )}
        {node.type === 'property_listing' && (
          <div style={sec('#CCFBF1', '#99F6E4')}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#0F766E', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 10 }}>🏠 Property Filters</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
              <div>
                <label style={lbl}>Property Type</label>
                <select style={inp} value={node.property_filters?.type || ''} onChange={(e) => u('property_filters', { ...node.property_filters, type: e.target.value || undefined })}>
                  <option value="">Any</option>
                  {PROPERTY_TYPES.map((t) => <option key={t} value={t.toLowerCase()}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Bedrooms</label>
                <select style={inp} value={node.property_filters?.bedrooms || ''} onChange={(e) => u('property_filters', { ...node.property_filters, bedrooms: e.target.value || undefined })}>
                  <option value="">Any</option>
                  {['1', '2', '3', '4'].map((b) => <option key={b} value={b}>{b} bed</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Location</label>
                <select style={inp} value={node.property_filters?.location || ''} onChange={(e) => u('property_filters', { ...node.property_filters, location: e.target.value || undefined })}>
                  <option value="">Any</option>
                  {LOCATION_OPTIONS.map((l) => <option key={l} value={l.toLowerCase().replace(/\s+/g, '_')}>{l}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <label style={lbl}>Layout</label>
                <select style={inp} value={node.listing_layout} onChange={(e) => u('listing_layout', e.target.value)}>
                  <option value="list">List</option>
                  <option value="grid">Grid</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Pin to Property</label>
                <select style={inp} value={node.property_id || ''} onChange={(e) => u('property_id', e.target.value || null)}>
                  <option value="">All matching</option>
                  {(propertyList || []).map((p) => <option key={p._id} value={p._id}>{p.title}</option>)}
                </select>
              </div>
            </div>
          </div>
        )}
        {node.type === 'conditional_branch' && (
          <div style={sec('#EEF2FF', '#C7D2FE')}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#4338CA', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 10 }}>🔀 Branch Options</div>
            <div style={{ marginBottom: 10 }}>
              <label style={lbl}>Variable Name</label>
              <input style={{ ...inp, fontFamily: 'monospace', fontSize: 11 }} value={node.branch_variable || ''} onChange={(e) => u('branch_variable', e.target.value.toLowerCase().replace(/\s+/g, '_'))} placeholder="property_type" />
            </div>
            <BranchOptionsEditor options={node.branch_options || []} allNodes={allNodes} onChange={(v) => u('branch_options', v)} />
          </div>
        )}
        {node.type === 'email_notify' && (
          <div style={sec('#E0F2FE', '#BAE6FD')}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#0369A1', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 10 }}>📧 Email Notification</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div><label style={lbl}>To Email</label><input type="email" style={inp} value={node.notify_email || ''} onChange={(e) => u('notify_email', e.target.value)} placeholder="team@company.com" /></div>
              <div><label style={lbl}>Recipient Name</label><input style={inp} value={node.notify_name || ''} onChange={(e) => u('notify_name', e.target.value)} placeholder="Team" /></div>
              <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Email Subject</label><input style={inp} value={node.notify_subject || ''} onChange={(e) => u('notify_subject', e.target.value)} placeholder="New inquiry from chatbot" /></div>
              <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Template Key</label><input style={{ ...inp, fontFamily: 'monospace', fontSize: 11 }} value={node.notify_template || 'CHATBOT_INQUIRY'} onChange={(e) => u('notify_template', e.target.value)} /></div>
            </div>
          </div>
        )}
        <div style={sec('#F8FAFC', '#E2E8F0')}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 10 }}>🔗 Navigation</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer', color: '#374151', marginBottom: 12, padding: '8px 10px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8 }}>
            <input type="checkbox" checked={node.show_children_as_options !== false} onChange={(e) => u('show_children_as_options', e.target.checked)} />
            <span style={{ fontWeight: 600 }}>Show child nodes as quick-reply buttons</span>
          </label>
          <div style={{ marginBottom: 12 }}>
            <label style={lbl}>Linked Children</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8, minHeight: 28 }}>
              {childIds.map((cid) => {
                const cn = allNodes.find((n) => n.id === cid);
                return (
                  <span key={cid} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 20, padding: '3px 10px', fontSize: 11, color: '#1D4ED8', fontWeight: 600 }}>
                    {cn ? `${cn.icon || ''} ${cn.label}` : `⚠️ ${cid.slice(0, 14)}…`}
                    <button type="button" onClick={() => removeChild(cid)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: 12, padding: 0 }}>×</button>
                  </span>
                );
              })}
              {childIds.length === 0 && <span style={{ fontSize: 11, color: '#CBD5E1', fontStyle: 'italic' }}>No children linked</span>}
            </div>
            <select style={{ ...inp, width: 'auto', minWidth: 240 }} defaultValue="" onChange={(e) => { if (e.target.value) { addChild(e.target.value); e.target.value = ''; } }}>
              <option value="">+ Link a child node…</option>
              {otherNodes.filter((n) => !childIds.includes(n.id)).map((n) => (
                <option key={n.id} value={n.id}>{n.icon || ''} {n.label} ({n.id})</option>
              ))}
            </select>
          </div>
          <div>
            <label style={lbl}>Auto-advance to Node (skip buttons)</label>
            <select style={inp} value={node.next_node_id || ''} onChange={(e) => u('next_node_id', e.target.value || null)}>
              <option value="">— show children as buttons (default) —</option>
              {otherNodes.map((n) => <option key={n.id} value={n.id}>{n.icon || ''} {n.label}</option>)}
            </select>
          </div>
        </div>
        <div style={sec('#FFFBEB', '#FDE68A')}>
          <TriggerEditor triggers={node.triggers || []} onChange={(v) => u('triggers', v)} />
        </div>
      </div>
    </div>
  );
};

const DRAG_THRESHOLD = 5;

const TreeNodeRow = ({ node, depth, selectedId, onSelect, onDuplicate, onDelete, onAddChild, allNodes, expandedIds, onToggleExpand, dragState, onDragStart }) => {
  const meta = nodeTypeMeta(node.type);
  const isGreeting = node.id === 'greeting';
  const isSelected = selectedId === node.id;
  const hasTriggers = (node.triggers || []).some((t) => t.enabled && ((t.keywords?.length || 0) + (t.patterns?.length || 0) + (t.exact_texts?.length || 0)) > 0);
  const hasChildren = node._children?.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const hasWarnings = !node.replies?.length || (node.type === 'form' && !node.form_fields?.length) || (node.type === 'booking_link' && !node.booking_url) || (node.type === 'conditional_branch' && !node.branch_options?.length);
  const isBeingDragged = dragState?.draggingId === node.id;
  const isDropTarget = dragState?.active && dragState?.hoverId === node.id && dragState?.draggingId !== node.id;
  return (
    <>
      <div style={{ paddingLeft: (depth || 0) * 16, marginBottom: 2, opacity: isBeingDragged ? 0.35 : 1, position: 'relative' }}>
        {(depth || 0) > 0 && (
          <div style={{ position: 'absolute', left: (depth || 0) * 16 - 10, top: 0, bottom: 0, width: 1, background: '#E2E8F0', pointerEvents: 'none' }} />
        )}
        <div
          data-nodeid={node.id}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '7px 8px', borderRadius: 8, cursor: 'pointer',
            background: isDropTarget ? '#DBEAFE' : isSelected ? `${meta.color}12` : 'transparent',
            border: `1.5px solid ${isDropTarget ? '#3B82F6' : isSelected ? meta.color + '50' : 'transparent'}`,
            outline: isDropTarget ? '2px dashed #3B82F6' : 'none', outlineOffset: 1,
            boxShadow: isSelected && !isDropTarget ? `0 2px 8px ${meta.color}20` : 'none',
            transition: 'background .1s, border .1s',
          }}
          onClick={() => onSelect(node.id)}
        >
          <div onPointerDown={(e) => { e.stopPropagation(); onDragStart(e, node.id); }} style={{ cursor: 'grab', fontSize: 14, color: '#CBD5E1', padding: '2px 3px', flexShrink: 0, lineHeight: 1, touchAction: 'none', userSelect: 'none' }} title="Drag onto another node to make it a child">⠿</div>
          <button type="button" onClick={(e) => { e.stopPropagation(); if (hasChildren) onToggleExpand(node.id); }} style={{ width: 18, height: 18, flexShrink: 0, borderRadius: 5, border: hasChildren ? `1px solid ${meta.color}40` : '1px solid transparent', background: hasChildren ? `${meta.color}15` : 'transparent', cursor: hasChildren ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: meta.color }}>
            {hasChildren ? (isExpanded ? '▾' : '▸') : ''}
          </button>
          <div style={{ width: 26, height: 26, borderRadius: 7, background: `${meta.color}18`, border: `1.5px solid ${meta.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>{node.icon || meta.icon}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, fontWeight: isSelected ? 700 : 500, color: isSelected ? '#0F172A' : '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 118 }}>{node.label || 'Untitled'}</span>
              {!node.enabled && <span style={{ fontSize: 8, fontWeight: 700, color: '#94A3B8', background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: 10, padding: '1px 4px' }}>OFF</span>}
              {hasTriggers && <span style={{ fontSize: 8, fontWeight: 700, color: '#D97706', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '1px 4px' }}>⚡</span>}
              {isGreeting && <span style={{ fontSize: 8, fontWeight: 700, color: '#6366F1', background: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: 10, padding: '1px 4px' }}>ROOT</span>}
              {hasWarnings && <span style={{ fontSize: 8, fontWeight: 700, color: '#D97706', background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 10, padding: '1px 4px' }}>⚠</span>}
            </div>
            <div style={{ fontSize: 10, color: meta.color, fontWeight: 600 }}>{meta.label}</div>
          </div>
          <div style={{ display: 'flex', gap: 2, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => onAddChild(node.id)} title="Add child" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#10B981', fontSize: 13, padding: '2px 3px', borderRadius: 4 }}>＋</button>
            <button type="button" onClick={() => onDuplicate(node.id)} title="Duplicate" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', fontSize: 11, padding: '2px 3px', borderRadius: 4 }}>⧉</button>
            {!isGreeting && <button type="button" onClick={() => onDelete(node.id)} title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: 11, padding: '2px 3px', borderRadius: 4 }}>✕</button>}
          </div>
        </div>
      </div>
    </>
  );
};

const FlowGraphBuilder = ({ nodes, onChange, fallbackNodeId, onFallbackChange, propertyList }) => {
  const [selectedId, setSelectedId] = useState('greeting');
  const [search, setSearch] = useState('');
  const [expandedIds, setExpandedIds] = useState(() => new Set(['greeting']));
  const [showFallback, setShowFallback] = useState(false);
  const [dragState, setDragState] = useState(null);
  const dragRef = useRef(null);
  const treeRef = useRef(null);
  const selectedNode = useMemo(() => nodes.find((n) => n.id === selectedId) || null, [nodes, selectedId]);
  const tree = useMemo(() => buildTree(nodes), [nodes]);
  const orphans = useMemo(() => {
    const referenced = getAllReferencedIds(nodes);
    return nodes.filter((n) => !referenced.has(n.id) && n.id !== (tree[0]?.id));
  }, [nodes, tree]);
  const flatTree = useMemo(() => {
    const result = [];
    const walk = (ns) => ns.forEach((n) => {
      result.push(n);
      if (expandedIds.has(n.id)) walk(n._children || []);
    });
    walk(tree);
    return result;
  }, [tree, expandedIds]);
  const handleUpdate = useCallback((updated) => {
    onChange(nodes.map((n) => n.id === updated.id ? updated : n));
  }, [nodes, onChange]);
  const handleToggleExpand = (id) => setExpandedIds((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const handleExpandAll = () => setExpandedIds(new Set(nodes.map((n) => n.id)));
  const handleCollapseAll = () => setExpandedIds(new Set(['greeting']));
  const handleAdd = useCallback((parentId = null) => {
    const existingIds = new Set(nodes.map((n) => n.id));
    const n = makeNode({ order: nodes.length }, parentId, existingIds);
    let newNodes = [...nodes, n];
    if (parentId) {
      newNodes = updateNodeInList(newNodes, parentId, (p) => ({
        ...p,
        children: [...(p.children || []), n.id]
      }));
      setExpandedIds((prev) => new Set([...prev, parentId]));
    }
    onChange(newNodes);
    setSelectedId(n.id);
  }, [nodes, onChange]);
  const handleDuplicate = useCallback((id) => {
    const src = nodes.find((n) => n.id === id);
    if (!src) return;
    const existingIds = new Set(nodes.map((n) => n.id));
    const copy = { ...src, id: generateNodeId(src.label + ' Copy', findParentId(nodes, id), existingIds), label: src.label + ' (copy)', children: [] };
    const parentId = findParentId(nodes, id);
    let newNodes = [...nodes, copy];
    if (parentId) {
      newNodes = updateNodeInList(newNodes, parentId, (p) => {
        const kids = [...(p.children || [])];
        const idx = kids.indexOf(id);
        if (idx !== -1) kids.splice(idx + 1, 0, copy.id);
        else kids.push(copy.id);
        return { ...p, children: kids };
      });
    }
    onChange(newNodes);
    setSelectedId(copy.id);
  }, [nodes, onChange]);
  const handleDelete = useCallback((id) => {
    if (selectedId === id) setSelectedId('greeting');
    onChange(deleteNodeFromList(nodes, id));
  }, [nodes, onChange, selectedId]);
  const isDescendant = useCallback((parentId, targetId) => {
    const visited = new Set();
    const check = (pid) => {
      if (visited.has(pid)) return false;
      visited.add(pid);
      const p = nodes.find((n) => n.id === pid);
      if (!p) return false;
      const kids = p.children || [];
      return kids.includes(targetId) || kids.some(check);
    };
    return check(parentId);
  }, [nodes]);
  const getNodeIdAtPoint = useCallback((x, y) => {
    if (!treeRef.current) return null;
    const els = treeRef.current.querySelectorAll('[data-nodeid]');
    for (const el of els) {
      const r = el.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return el.dataset.nodeid;
    }
    return null;
  }, []);
  const onDragStart = useCallback((e, nodeId) => {
    e.preventDefault();
    const initial = { draggingId: nodeId, startX: e.clientX, startY: e.clientY, active: false, ghostX: e.clientX + 14, ghostY: e.clientY + 14, hoverId: null };
    dragRef.current = { ...initial };
    setDragState({ ...initial });
    const onMove = (me) => {
      const cur = dragRef.current;
      if (!cur) return;
      const dx = me.clientX - cur.startX, dy = me.clientY - cur.startY;
      const active = cur.active || Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD;
      const hoverId = active ? getNodeIdAtPoint(me.clientX, me.clientY) : cur.hoverId;
      const next = { ...cur, active, ghostX: me.clientX + 14, ghostY: me.clientY + 14, hoverId };
      dragRef.current = next;
      setDragState({ ...next });
    };
    const onUp = () => {
      const cur = dragRef.current;
      if (cur?.active && cur.hoverId && cur.hoverId !== cur.draggingId && !isDescendant(cur.draggingId, cur.hoverId)) {
        const newParentId = cur.hoverId;
        const draggingId = cur.draggingId;
        setDragState(null);
        dragRef.current = null;
        onChange((prevNodes) => {
          let updated = prevNodes.map((n) => ({
            ...n,
            children: (n.children || []).filter((cid) => cid !== draggingId)
          }));
          updated = updated.map((n) => {
            if (n.id !== newParentId) return n;
            const kids = n.children || [];
            if (kids.includes(draggingId)) return n;
            return { ...n, children: [...kids, draggingId] };
          });
          return updated;
        });
        setExpandedIds((prev) => new Set([...prev, newParentId]));
        const parentLabel = nodes.find((n) => n.id === newParentId)?.label || newParentId;
        toast.success(`Moved under "${parentLabel}"`, { autoClose: 1500 });
      } else {
        dragRef.current = null;
        setDragState(null);
      }
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [nodes, onChange, isDescendant, getNodeIdAtPoint]);
  const filteredNodes = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.toLowerCase();
    return nodes.filter((n) => n.label?.toLowerCase().includes(q) || n.id?.toLowerCase().includes(q) || n.type?.toLowerCase().includes(q));
  }, [nodes, search]);
  const calcDepth = useCallback((id) => {
    const find = (ns, target, d) => {
      for (const n of ns) {
        if (n.id === target) return d;
        const r = find(n._children || [], target, d + 1);
        if (r !== -1) return r;
      }
      return -1;
    };
    const d = find(tree, id, 0);
    return d === -1 ? 0 : d;
  }, [tree]);
  return (
    <div style={{ display: 'flex', height: '100%', position: 'relative' }}>
      {dragState?.active && (
        <div style={{ position: 'fixed', left: dragState.ghostX, top: dragState.ghostY, zIndex: 99999, pointerEvents: 'none', background: '#1D4ED8', color: '#fff', borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 700, boxShadow: '0 8px 24px rgba(0,0,0,0.25)', opacity: 0.92, whiteSpace: 'nowrap', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: 6 }}>
          {(() => { const n = nodes.find((x) => x.id === dragState.draggingId); return n ? `${n.icon || ''} ${n.label}` : ''; })()}
          {dragState.hoverId && dragState.hoverId !== dragState.draggingId && (
            <span style={{ fontSize: 10, opacity: 0.75 }}>→ {nodes.find((x) => x.id === dragState.hoverId)?.label || ''}</span>
          )}
        </div>
      )}
      <div style={{ width: 300, flexShrink: 0, borderRight: '1px solid #E2E8F0', background: '#FAFBFF', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '10px 10px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: '#334155' }}>Flow Tree <span style={{ color: '#94A3B8', fontWeight: 500 }}>({nodes.length})</span></span>
            <button type="button" onClick={() => handleAdd(null)} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 7, border: '1.5px solid #BFDBFE', background: '#EFF6FF', color: '#1D4ED8', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>+ Node</button>
          </div>
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <input style={{ ...inp, fontSize: 11, paddingLeft: 30 }} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search nodes…" />
            <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: '#94A3B8', pointerEvents: 'none' }}>🔍</span>
            {search && <button type="button" onClick={() => setSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>}
          </div>
          {!search.trim() && (
            <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
              <button type="button" onClick={handleExpandAll} style={{ flex: 1, padding: '4px 6px', borderRadius: 6, border: '1px solid #E2E8F0', background: '#fff', color: '#64748B', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>Expand All</button>
              <button type="button" onClick={handleCollapseAll} style={{ flex: 1, padding: '4px 6px', borderRadius: 6, border: '1px solid #E2E8F0', background: '#fff', color: '#64748B', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>Collapse All</button>
            </div>
          )}
          <div style={{ marginBottom: 8 }}>
            <button type="button" onClick={() => setShowFallback((o) => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, cursor: 'pointer' }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#B45309', textTransform: 'uppercase', letterSpacing: '.5px', flex: 1, textAlign: 'left' }}>🛡️ Global Fallback</span>
              <span style={{ fontSize: 10, color: '#B45309' }}>{showFallback ? '▾' : '▸'}</span>
            </button>
            {showFallback && (
              <div style={{ padding: '8px 10px', background: '#FFFBEB', border: '1px solid #FDE68A', borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
                <select style={{ ...inp, fontSize: 11 }} value={fallbackNodeId || ''} onChange={(e) => onFallbackChange(e.target.value || null)}>
                  <option value="">— none (AI / default) —</option>
                  {nodes.map((n) => <option key={n.id} value={n.id}>{n.icon || ''} {n.label}</option>)}
                </select>
                <p style={{ fontSize: 9, color: '#92400E', marginTop: 3, marginBottom: 0 }}>Fires when no trigger matches</p>
              </div>
            )}
          </div>
          {!search.trim() && (
            <div style={{ padding: '5px 8px', background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 7, marginBottom: 8 }}>
              <p style={{ fontSize: 9, color: '#0369A1', margin: 0, lineHeight: 1.5 }}><strong>⠿ Drag</strong> any node and drop it onto another to reparent.</p>
            </div>
          )}
        </div>
        <div ref={treeRef} style={{ flex: 1, overflowY: 'auto', padding: '0 10px 10px' }}>
          {search.trim() ? (
            filteredNodes?.length > 0 ? filteredNodes.map((node) => {
              const meta = nodeTypeMeta(node.type);
              const isSel = selectedId === node.id;
              return (
                <div key={node.id} data-nodeid={node.id} onClick={() => setSelectedId(node.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 8px', borderRadius: 8, marginBottom: 3, cursor: 'pointer', background: isSel ? `${meta.color}12` : '#fff', border: `1.5px solid ${isSel ? meta.color + '50' : '#E2E8F0'}` }}>
                  <div style={{ width: 26, height: 26, borderRadius: 7, background: `${meta.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>{node.icon || meta.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>{node.label}</div>
                    <div style={{ fontSize: 10, color: meta.color }}>{meta.label}</div>
                  </div>
                </div>
              );
            }) : <div style={{ textAlign: 'center', padding: 20, color: '#CBD5E1', fontSize: 12 }}>No nodes found.</div>
          ) : (
            <>
              {flatTree.map((node) => (
                <TreeNodeRow
                  key={node.id}
                  node={node}
                  depth={calcDepth(node.id)}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  onDuplicate={handleDuplicate}
                  onDelete={handleDelete}
                  onAddChild={handleAdd}
                  allNodes={nodes}
                  expandedIds={expandedIds}
                  onToggleExpand={handleToggleExpand}
                  dragState={dragState}
                  onDragStart={onDragStart}
                />
              ))}
            </>
          )}
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', background: '#fff', display: 'flex', flexDirection: 'column' }}>
        <NodeEditor
          node={selectedNode}
          onUpdate={handleUpdate}
          onClose={() => setSelectedId(null)}
          allNodes={nodes}
          propertyList={propertyList}
        />
      </div>
    </div>
  );
};

const ChatbotConfigModal = ({ mode, open, onClose, onSaved, existingConfig, propertyList }) => {
  const [values, setValues] = useState(emptyConfig());
  const [saving, setSaving] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [activeTab, setActiveTab] = useState('branding');
  const isEditMode = mode === 'edit';
  const fetchFreshConfig = useCallback(async (configId) => {
    if (!configId) return;
    setLoadingConfig(true);
    try {
      const res = await getChatbotConfigById(configId);
      const fresh = res?.data;
      if (fresh) {
        const cleanNodes = Array.from(new Map(fresh.flow_nodes?.map(n => [n.id, n]) || []).values());
        setValues({
          chat_slug: fresh.chat_slug || '',
          branding: { ...emptyConfig().branding, ...(fresh.branding || {}) },
          flow_nodes: cleanNodes.length ? cleanNodes : makeStarterFlow(),
          fallback_node_id: fresh.fallback_node_id || 'fallback_inquiry_form',
          integrations: { ...emptyConfig().integrations, ...(fresh.integrations || {}) },
          ai: { ...emptyConfig().ai, ...(fresh.ai || {}) }
        });
      }
    } catch (error) {
      toast.error('Failed to load chatbot configuration');
    } finally {
      setLoadingConfig(false);
    }
  }, []);
  useEffect(() => {
    if (!open) return;
    if (isEditMode && existingConfig?._id) {
      fetchFreshConfig(existingConfig._id);
    } else {
      setValues(emptyConfig());
    }
    setActiveTab('branding');
  }, [open, mode, existingConfig, fetchFreshConfig]);
  const set = useCallback((path, value) => {
    setValues((prev) => {
      const keys = path.split('.');
      if (keys.length === 1) return { ...prev, [keys[0]]: value };
      if (keys.length === 2) return { ...prev, [keys[0]]: { ...prev[keys[0]], [keys[1]]: value } };
      if (keys.length === 3) return { ...prev, [keys[0]]: { ...prev[keys[0]], [keys[1]]: { ...prev[keys[0]][keys[1]], [keys[2]]: value } } };
      return prev;
    });
  }, []);
  const handleFlowChange = useCallback((nodesOrUpdater) => {
    if (typeof nodesOrUpdater === 'function') {
      setValues((prev) => ({ ...prev, flow_nodes: nodesOrUpdater(prev.flow_nodes) }));
    } else {
      setValues((prev) => ({ ...prev, flow_nodes: nodesOrUpdater }));
    }
  }, []);
  const buildingOptions = useMemo(() =>
    (propertyList || []).find((p) => p._id === values.integrations.property_id)?.building?.filter((b) => b?.buildingName)?.map((b) => b.buildingName) || [],
    [propertyList, values.integrations.property_id]
  );
  const handleSubmit = async () => {
    if (!values.chat_slug?.trim()) { toast.error('Chat slug is required'); return; }
    let fv = { ...values };
    if (!fv.branding.bot_slug?.trim() && fv.branding.bot_name) {
      fv = { ...fv, branding: { ...fv.branding, bot_slug: fv.branding.bot_name.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-') } };
    }
    setSaving(true);
    try {
      const payload = {
        chat_slug: fv.chat_slug.trim().toLowerCase(),
        branding: fv.branding,
        flow_nodes: fv.flow_nodes,
        fallback_node_id: fv.fallback_node_id || 'fallback_inquiry_form',
        integrations: fv.integrations,
        ai: fv.ai
      };
      if (isEditMode && existingConfig?._id) {
        await updateChatbotConfig(existingConfig._id, payload);
        toast.success('Chatbot config updated');
      } else {
        await createChatbotConfig(payload);
        toast.success('Chatbot config created');
      }
      if (onSaved) onSaved();
      onClose();
    } catch { }
    finally { setSaving(false); }
  };
  const tabs = [
    { key: 'branding', label: '🎨 Branding' },
    { key: 'flow', label: '🌿 Flow Builder' },
    { key: 'integrations', label: '🔗 Integrations' },
    { key: 'ai', label: '🤖 AI' },
  ];
  const tabStyle = (key) => ({
    padding: '9px 16px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', border: 'none',
    borderBottom: activeTab === key ? '2px solid #1D4ED8' : '2px solid transparent',
    background: 'transparent', color: activeTab === key ? '#1D4ED8' : '#64748B',
    transition: 'all 0.12s ease', whiteSpace: 'nowrap'
  });
  return (
    <Modal isOpen={open} toggle={onClose} size="xl" centered style={{ maxWidth: '96vw' }}>
      <Form>
        <ModalHeader toggle={onClose} style={{ background: '#0F1C2E', borderBottom: 'none', padding: '14px 20px' }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: '#F0F9FF' }}>
            {isEditMode ? `✏️ Edit: ${values.branding?.bot_name || 'Chatbot'}` : '✨ Create New Chatbot'}
          </span>
        </ModalHeader>
        <div style={{ borderBottom: '1px solid #E2E8F0', display: 'flex', padding: '0 16px', background: '#F8FAFC', overflowX: 'auto' }}>
          {tabs.map((t) => <button key={t.key} type="button" style={tabStyle(t.key)} onClick={() => setActiveTab(t.key)}>{t.label}</button>)}
        </div>
        <ModalBody style={{ padding: 0, height: '70vh', overflow: 'hidden' }}>
          {loadingConfig ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Spinner />
            </div>
          ) : (
            <>
              {activeTab === 'flow' && (
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ background: '#EFF6FF', borderBottom: '1px solid #BFDBFE', padding: '7px 16px', fontSize: 11, color: '#1E40AF', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <span style={{ fontWeight: 700, color: '#1D4ED8' }}>Flow Builder</span>
                    <span style={{ color: '#93C5FD' }}>·</span>
                    <span>Click <strong style={{ color: '#1D4ED8' }}>＋</strong> to add children · <strong style={{ color: '#1D4ED8' }}>⠿ drag</strong> a node onto another to reparent · <strong style={{ color: '#1D4ED8' }}>⚡</strong> for keyword triggers</span>
                  </div>
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <FlowGraphBuilder
                      nodes={values.flow_nodes}
                      onChange={handleFlowChange}
                      propertyList={propertyList}
                      fallbackNodeId={values.fallback_node_id}
                      onFallbackChange={(v) => setValues((p) => ({ ...p, fallback_node_id: v }))}
                    />
                  </div>
                </div>
              )}
              {activeTab === 'branding' && (
                <div style={{ padding: '20px 24px', overflowY: 'auto', height: '100%' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div>
                      <label style={lbl}>Bot Name *</label>
                      <input style={inp} value={values.branding.bot_name} onChange={(e) => {
                        const n = e.target.value;
                        set('branding.bot_name', n);
                        if (!isEditMode) set('branding.bot_slug', n.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-'));
                      }} placeholder="Chatbot" />
                    </div>
                    <div>
                      <label style={lbl}>Chat Slug * (public URL)</label>
                      <input style={inp} value={values.chat_slug} onChange={(e) => set('chat_slug', e.target.value.toLowerCase().trim())} placeholder="your-company" />
                      <p style={{ fontSize: 10, color: '#94A3B8', marginTop: 3 }}>e.g. /chat/your-company</p>
                    </div>
                    <div><label style={lbl}>Logo Icon (emoji)</label><input style={inp} value={values.branding.logo_icon} onChange={(e) => set('branding.logo_icon', e.target.value)} placeholder="🤖" /></div>
                    <div><label style={lbl}>Logo Icon URL</label><input style={inp} value={values.branding.logo_icon_url} onChange={(e) => set('branding.logo_icon_url', e.target.value)} placeholder="https://…" /></div>
                    <div><label style={lbl}>Launcher Icon (emoji)</label><input style={inp} value={values.branding.launcher_icon} onChange={(e) => set('branding.launcher_icon', e.target.value)} /></div>
                    <div><label style={lbl}>Launcher Icon URL</label><input style={inp} value={values.branding.launcher_icon_url} onChange={(e) => set('branding.launcher_icon_url', e.target.value)} placeholder="https://…" /></div>
                    <div><label style={lbl}>Closing Icon (emoji)</label><input style={inp} value={values.branding.closing_icon} onChange={(e) => set('branding.closing_icon', e.target.value)} /></div>
                    <div><label style={lbl}>Closing Icon URL</label><input style={inp} value={values.branding.closing_icon_url} onChange={(e) => set('branding.closing_icon_url', e.target.value)} placeholder="https://…" /></div>
                    <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Input Placeholder</label><input style={inp} value={values.branding.placeholder_text} onChange={(e) => set('branding.placeholder_text', e.target.value)} /></div>
                    {[['primary_color', 'Primary Color'], ['secondary_color', 'Secondary Color'], ['text_color', 'Text Color'], ['background_color', 'Background Color']].map(([k, label]) => (
                      <div key={k}>
                        <label style={lbl}>{label}</label>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <input type="color" style={{ width: 42, height: 34, padding: 2, border: '1.5px solid #E2E8F0', borderRadius: 6, cursor: 'pointer' }} value={values.branding[k]} onChange={(e) => set(`branding.${k}`, e.target.value)} />
                          <input style={{ ...inp, flex: 1, fontFamily: 'monospace', fontSize: 11 }} value={values.branding[k]} onChange={(e) => set(`branding.${k}`, e.target.value)} />
                        </div>
                      </div>
                    ))}
                    <div style={{ gridColumn: '1/-1', display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 8, alignItems: 'end' }}>
                      <div><label style={lbl}>Width (px)</label><input type="number" style={inp} value={values.branding.chat_window_width} onChange={(e) => set('branding.chat_window_width', parseInt(e.target.value) || 380)} /></div>
                      <div><label style={lbl}>Height (px)</label><input type="number" style={inp} value={values.branding.chat_window_height} onChange={(e) => set('branding.chat_window_height', parseInt(e.target.value) || 600)} /></div>
                      <div><label style={lbl}>Position</label><select style={inp} value={values.branding.position} onChange={(e) => set('branding.position', e.target.value)}><option value="bottom-right">Bottom Right</option><option value="bottom-left">Bottom Left</option></select></div>
                      <div><label style={lbl}>Offset X (px)</label><input type="number" style={inp} value={values.branding.offset_x} onChange={(e) => set('branding.offset_x', parseInt(e.target.value) || 20)} /></div>
                      <div><label style={lbl}>Offset Y (px)</label><input type="number" style={inp} value={values.branding.offset_y} onChange={(e) => set('branding.offset_y', parseInt(e.target.value) || 20)} /></div>
                    </div>
                    <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Font Family</label><input style={inp} value={values.branding.font_family} onChange={(e) => set('branding.font_family', e.target.value)} /></div>
                    <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Custom CSS</label><textarea style={{ ...inp, resize: 'vertical', minHeight: 64, fontFamily: 'monospace', fontSize: 11 }} value={values.branding.custom_css} onChange={(e) => set('branding.custom_css', e.target.value)} placeholder=".cb-window { border-radius: 20px; }" /></div>
                  </div>
                </div>
              )}
              {activeTab === 'integrations' && (
                <div style={{ padding: '20px 24px', overflowY: 'auto', height: '100%' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div style={{ gridColumn: '1/-1' }}>
                      <label style={lbl}>Property</label>
                      <select style={inp} value={values.integrations.property_id} onChange={(e) => { set('integrations.property_id', e.target.value); set('integrations.building_name', ''); }}>
                        <option value="">None</option>
                        {(propertyList || []).map((p) => <option key={p._id} value={p._id}>{p.title}{p.location ? `, ${p.location}` : ''}</option>)}
                      </select>
                    </div>
                    {buildingOptions.length > 0 && (
                      <div style={{ gridColumn: '1/-1' }}>
                        <label style={lbl}>Building</label>
                        <select style={inp} value={values.integrations.building_name} onChange={(e) => set('integrations.building_name', e.target.value)}>
                          <option value="">All Buildings</option>
                          {buildingOptions.map((b) => <option key={b} value={b}>{b}</option>)}
                        </select>
                      </div>
                    )}
                    <div style={{ gridColumn: '1/-1', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#EFF6FF', borderRadius: 9, border: '1px solid #BFDBFE' }}>
                      <input type="checkbox" id="suite_api" checked={values.integrations.suite_api_enabled} onChange={(e) => set('integrations.suite_api_enabled', e.target.checked)} />
                      <label htmlFor="suite_api" style={{ fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer', margin: 0 }}>Enable Suite Inspection Lookup</label>
                    </div>
                    <div><label style={lbl}>Global Booking URL</label><input style={inp} value={values.integrations.booking_url} onChange={(e) => set('integrations.booking_url', e.target.value)} placeholder="https://calendly.com/…" /><p style={{ fontSize: 10, color: '#94A3B8', marginTop: 3 }}>Used by the Book Appointment node</p></div>
                    <div><label style={lbl}>Resident Application URL</label><input style={inp} value={values.integrations.resident_application_url || ''} onChange={(e) => set('integrations.resident_application_url', e.target.value)} placeholder="https://yoursite.com/apply" /></div>
                    <div><label style={lbl}>Properties Page URL</label><input style={inp} value={values.integrations.properties_page_url || ''} onChange={(e) => set('integrations.properties_page_url', e.target.value)} placeholder="https://yoursite.com/properties" /></div>
                    <div><label style={lbl}>Blog URL</label><input style={inp} value={values.integrations.blog_url || ''} onChange={(e) => set('integrations.blog_url', e.target.value)} placeholder="https://yoursite.com/blog" /></div>
                    <div><label style={lbl}>CRM Webhook URL</label><input style={inp} value={values.integrations.crm_webhook} onChange={(e) => set('integrations.crm_webhook', e.target.value)} placeholder="https://your-crm.com/webhook" /></div>
                    <div><label style={lbl}>Notification Email (general)</label><input type="email" style={inp} value={values.integrations.notification_email} onChange={(e) => set('integrations.notification_email', e.target.value)} placeholder="manager@company.com" /></div>
                    <div>
                      <label style={lbl}>Team Email (for all forms)</label>
                      <input type="email" style={inp} value={values.integrations.margo_email || ''} onChange={(e) => set('integrations.margo_email', e.target.value)} placeholder="team@company.com" />
                      <p style={{ fontSize: 10, color: '#94A3B8', marginTop: 3 }}>Receives all form submissions</p>
                    </div>
                  </div>
                </div>
              )}
              {activeTab === 'ai' && (
                <div style={{ padding: '20px 24px', overflowY: 'auto', height: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#F5F3FF', borderRadius: 9, border: '1px solid #DDD6FE', marginBottom: 16 }}>
                    <input type="checkbox" id="ai_enabled" checked={values.ai.enabled} onChange={(e) => set('ai.enabled', e.target.checked)} />
                    <label htmlFor="ai_enabled" style={{ fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer', margin: 0 }}>Enable AI Fallback (fires when no trigger or node matches)</label>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div>
                      <label style={lbl}>Claude Model</label>
                      <select style={inp} value={values.ai.model} onChange={(e) => set('ai.model', e.target.value)} disabled={!values.ai.enabled}>
                        {AI_MODELS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                    </div>
                    <div><label style={lbl}>Max Tokens</label><input type="number" style={inp} value={values.ai.max_tokens} min={50} max={4096} onChange={(e) => set('ai.max_tokens', parseInt(e.target.value) || 300)} disabled={!values.ai.enabled} /></div>
                    <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Fallback Message (shown when AI disabled or fails)</label><input style={inp} value={values.ai.fallback_message} onChange={(e) => set('ai.fallback_message', e.target.value)} /></div>
                    <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Knowledge Base (injected into system prompt)</label><textarea style={{ ...inp, resize: 'vertical', minHeight: 80 }} value={values.ai.knowledge_base} onChange={(e) => set('ai.knowledge_base', e.target.value)} placeholder="Describe your company, services, policies, FAQs…" disabled={!values.ai.enabled} /></div>
                    <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Custom System Prompt (leave blank for default)</label><textarea style={{ ...inp, resize: 'vertical', minHeight: 64 }} value={values.ai.system_prompt} onChange={(e) => set('ai.system_prompt', e.target.value)} placeholder="You are a helpful assistant for your business…" disabled={!values.ai.enabled} /></div>
                  </div>
                </div>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter style={{ background: '#F8FAFC', borderTop: '1px solid #E2E8F0' }}>
          <Button outline color="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button color="primary" onClick={handleSubmit} disabled={saving} style={{ minWidth: 120 }}>
            {saving && <Spinner size="sm" className="me-2" />}
            {saving ? 'Saving…' : isEditMode ? 'Update Config' : 'Create Config'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};
export { ChatbotConfigModal };